import { cva, type VariantProps } from 'class-variance-authority';
import { AlertCircle, CheckCircle2, Info, TriangleAlert } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/shared/lib/cn';

const alertVariants = cva('flex w-full items-start gap-3 rounded-lg border p-3.5 text-sm', {
  variants: {
    variant: {
      info: 'border-info/25 bg-info-subtle text-foreground [&>svg]:text-info',
      success: 'border-success/25 bg-success-subtle text-foreground [&>svg]:text-success',
      warning: 'border-warning/30 bg-warning-subtle text-foreground [&>svg]:text-warning',
      destructive:
        'border-destructive/25 bg-destructive-subtle text-foreground [&>svg]:text-destructive',
    },
  },
  defaultVariants: { variant: 'info' },
});

const DEFAULT_ICONS = {
  info: Info,
  success: CheckCircle2,
  warning: TriangleAlert,
  destructive: AlertCircle,
} as const;

export interface AlertProps
  // `title` is redefined as rich content, so the DOM attribute of the same
  // name (a plain tooltip string) is excluded rather than shadowed.
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'title'>, VariantProps<typeof alertVariants> {
  title?: React.ReactNode;
  /** Pass `null` to hide the leading icon. */
  icon?: React.ReactNode | null;
}

export function Alert({
  className,
  variant = 'info',
  title,
  icon,
  children,
  ...props
}: AlertProps) {
  const Icon = DEFAULT_ICONS[variant ?? 'info'];
  return (
    <div
      role={variant === 'destructive' ? 'alert' : 'status'}
      className={cn(alertVariants({ variant }), className)}
      {...props}
    >
      {icon === null ? null : (icon ?? <Icon className="mt-0.5 size-4.5 shrink-0" />)}
      <div className="min-w-0 flex-1 space-y-1">
        {title ? <p className="leading-tight font-semibold">{title}</p> : null}
        {children ? (
          <div className="text-muted-foreground text-[13px] leading-relaxed">{children}</div>
        ) : null}
      </div>
    </div>
  );
}
